import { useSelector } from 'react-redux';
import { uniqBy, size } from 'global/lib/lodash';
import { useMemo } from 'react';
import isLoggedIn from 'global/utils/isLoggedIn';
import { ALLREALMS } from '../components/droprealms';
import { explicitRealmsBelongingToBuilder } from './explicitRealmsBelongingToBuilder';

const getHighestTier = (realms) =>
  realms.reduce((tier, r) => (r.tier === 'builder' ? 'builder' : tier), 'division');

const getAncestors = (realm) => {
  const ancestors = [];
  let current = ALLREALMS[realm.parentId];

  while (current && current.tier !== 'builder') {
    ancestors.push(current);
    current = ALLREALMS[current.parentId];
  }
  if (current) ancestors.push(current);

  return ancestors;
};

export const useUser = () => {
  const auth = useSelector((s) => s.auth);
  const me = auth?.user;
  const realm = auth?.realm;
  const realms = auth?.user?.realms;

  // ---- Access-related data ----
  const userExplicitRealms = realms || [];
  const userRealmTier = useMemo( () => getHighestTier(userExplicitRealms), [userExplicitRealms] );
  const userBuilderRealm = me?.realm || {};
  const userBuilderRealms = useMemo(
    () => uniqBy(
      [
        userBuilderRealm,
        ...userExplicitRealms.filter((r) => r.tier === 'builder'),
      ],
      (r) => r.id
    ).filter(Boolean),
    [userBuilderRealm, userExplicitRealms]
  );
  const userExplicitCompanyRealms = useMemo(
    () => explicitRealmsBelongingToBuilder(userExplicitRealms, userBuilderRealm),
    [userExplicitRealms, userBuilderRealm]
  );

  // ---- Context-related data ----
  const siteContextSelectedRealm = realm;
  const siteContextRealmTier = realm?.tier || userRealmTier;
  const siteContextRealms = useMemo(() => {
    if (!size(ALLREALMS)) return [];
    const context = realm
      ? [realm]
      : userExplicitRealms.filter((r) => r.tier === userRealmTier);

    return [...context, ...context.flatMap(getAncestors)];
  }, [realm, userExplicitRealms]);

  return {
    me,
    loggedIn: isLoggedIn(auth),
    // TODO: Verify if realm/userRealm are needed on Native and remove in favor of siteContextSelectedRealm and userBuilderRealm
    realm: realm,
    userRealm: me?.realm || {},

    // Access
    userExplicitRealms,
    userExplicitCompanyRealms,
    userRealmTier,
    userBuilderRealm,
    userBuilderRealms,

    // Context
    siteContextSelectedRealm,
    siteContextRealms,
    siteContextRealmTier,

    allrealms: ALLREALMS,
  };
};
